import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

import DraftSummaryItemAccordion from './DraftSummaryItemAccordion';
import type { DraftSummarySection } from './buildDraftSummary';

interface Props {
  errorMessage?: string | null;
  onGoToStep: (() => void) | null;
  rollbackErrors?: Array<string>;
  section: DraftSummarySection;
}

export default function DraftSummaryAccordion({ errorMessage, onGoToStep, rollbackErrors = [], section }: Props) {
  const hasCleanupIssue = rollbackErrors.length > 0;

  return (
    <Accordion
      variant="outlined"
      disableGutters
      key={`${section.status ?? 'idle'}-${hasCleanupIssue}`}
      defaultExpanded={section.status === 'error' || hasCleanupIssue}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }} gap={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2">{section.title}</Typography>
            {section.status === 'success' && <Chip label="Passed" color="success" size="small" variant="outlined" />}
            {section.status === 'error' && <Chip label="Failed" color="error" size="small" variant="outlined" />}
            {hasCleanupIssue && <Chip label="Needs review" color="warning" size="small" variant="outlined" />}
          </Stack>
          <Typography variant="body2" fontWeight={600}>
            {section.summary}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {errorMessage && (
          <Alert
            severity="error"
            action={
              onGoToStep ? (
                <Button color="inherit" size="small" onClick={onGoToStep}>
                  Go to step
                </Button>
              ) : undefined
            }
          >
            <Typography variant="body2">{errorMessage}</Typography>
            {!onGoToStep && (
              <Typography variant="caption" color="text.secondary">
                This isn't something you can fix from this wizard -- screenshot this message for support.
              </Typography>
            )}
          </Alert>
        )}
        {hasCleanupIssue && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight={600}>
              Cleanup didn't fully complete and may need manual review:
            </Typography>
            <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
              {rollbackErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </Box>
          </Alert>
        )}
        {section.items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing to show.
          </Typography>
        ) : (
          section.items.map((item, index) => <DraftSummaryItemAccordion key={`${item.primary}-${index}`} item={item} />)
        )}
      </AccordionDetails>
    </Accordion>
  );
}
